import React, { useState } from 'react';
import { Button, ClickAwayListener, Popper, Box, Typography, Stack, CircularProgress, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NoteIcon from '@mui/icons-material/Note';
import { useFetchCompanyNotesQuery } from '../../../../../services/companyApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { FormattedMessage } from 'react-intl';
import CompanyNotesList from './CompanyNotesList';

interface CompanyNotesPopperProps {
	onNoteSelect?: (note: any) => void;
}

export const CompanyNotesPopper: React.FC<CompanyNotesPopperProps> = ({ onNoteSelect }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const selectedCompany = useSelector((state: RootState) => state.companySearch.selected);
	const {
		data: companyNotes,
		isLoading,
		isError,
	} = useFetchCompanyNotesQuery({ companyId: selectedCompany?.companyId ?? '' }, { skip: !selectedCompany?.companyId });

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const isDisabled = !selectedCompany?.companyId;

	const button = (
		<Button
			onClick={handleClick}
			variant='text'
			startIcon={open ? <CloseIcon /> : <NoteIcon />}
			aria-expanded={Boolean(anchorEl)}
			aria-haspopup='true'
			disabled={isDisabled}
		>
			<FormattedMessage id='companyNotes.buttonText' defaultMessage='Notes' />
		</Button>
	);

	return (
		<ClickAwayListener onClickAway={handleClose}>
			<div style={{ position: 'relative', zIndex: 9999 }}>
				{isDisabled ? (
					<Tooltip title={<FormattedMessage id='companyNotes.selectCompanyFirst' defaultMessage='Select a company first' />}>
						<span>{button}</span>
					</Tooltip>
				) : (
					button
				)}
				<Popper
					open={open}
					anchorEl={anchorEl}
					placement='bottom-start'
					sx={{
						zIndex: 99999,
						width: anchorEl ? Math.max(anchorEl.getBoundingClientRect().width, 500) : '25rem',
						minWidth: '300px',
						maxWidth: '90vw',
					}}
				>
					<Box
						sx={{
							border: 1,
							borderColor: 'divider',
							bgcolor: 'background.paper',
							boxShadow: 3,
							borderRadius: 1,
							mt: 1,
							maxHeight: '400px',
							overflow: 'auto',
							p: 2,
						}}
					>
						<Stack direction='row' alignItems='space-between' sx={{ mb: 2 }}>
							<Typography sx={{ flexGrow: 1, fontSize: '1.15rem', fontWeight: 600 }}>
								<FormattedMessage id='companyNotes.title' defaultMessage='Company Notes' />
							</Typography>
						</Stack>

						{isLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
								<CircularProgress />
							</Box>
						) : isError ? (
							<Typography color='error'>
								<FormattedMessage id='companyNotes.error' defaultMessage='Error loading notes' />
							</Typography>
						) : (
							<CompanyNotesList
								notes={companyNotes ?? []}
								onNoteSelect={(note: any) => {
									if (onNoteSelect) {
										onNoteSelect(note);
									}
									handleClose();
								}}
							/>
						)}
					</Box>
				</Popper>
			</div>
		</ClickAwayListener>
	);
};
